import { Suspense, forwardRef, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FormikProps } from "formik"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"

import {
    CompanyAdminWorkgroupsQuery,
    WorkgroupForCompanyResponse,
    fetchWorkgroupForCompany,
    updateWorkgroupForCompany,
} from "../workgroupsApi"
import { WorkgroupFormValues } from "../workgroupsTypes"
import { CreateUpdateWorkgroupForm } from "../components/CreateUpdateWorkgroupForm"

const selectWorkgroup = (data: WorkgroupForCompanyResponse) => {
    const workgroup = data.workgroup
    if (!workgroup) return null

    const initialValues: WorkgroupFormValues = {
        name: workgroup.name,
        description: workgroup.description ?? "",
        calendarEmailAddress: workgroup.calendarEmailAddress ?? "",
        isActive: workgroup.isActive,
        members: workgroup.members ?? [],
    }

    return { initialValues, history: workgroup.history }
}

const Workgroup = forwardRef<FormikProps<WorkgroupFormValues>>((props, ref) => {
    const { workgroupId } = useParams()
    const navigate = useNavigate()

    const workgroupResult = useApiQuery(fetchWorkgroupForCompany, {
        queryName: CompanyAdminWorkgroupsQuery.DETAILS,
        dependencies: {
            workgroupId: workgroupId || "",
        },
        enabled: Boolean(workgroupId),
        select: selectWorkgroup,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutate: callUpdateWorkgroup } = useApiMutation(updateWorkgroupForCompany, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminWorkgroupsQuery.LIST] })
            navigate(COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_ROUTE)
        },
    })

    return (
        <CreateUpdateWorkgroupForm
            ref={ref}
            initialValues={workgroupResult.data?.initialValues}
            history={workgroupResult.data?.history}
            onSubmit={(data) => {
                workgroupId && callUpdateWorkgroup({ workgroup: data, workgroupId })
            }}
        />
    )
})

export function EditWorkgroup() {
    const workgroupFormRef = useRef<FormikProps<WorkgroupFormValues>>(null)

    const handleSaveClick = () => {
        workgroupFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="edit-workgroup-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_EDIT}
                    title="Edit workgroup"
                    rightElement={
                        <LocalizedStrict id="edit-workgroup-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-workgroups-title">
                            <BreadcrumbLink to={COMPANY_ADMIN_ORGANIZATION_WORKGROUPS_ROUTE}>Workgroups</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-workgroups-edit-title">
                            <Breadcrumb>Edit workgroup</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Workgroup ref={workgroupFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
